import React from 'react';
import { GiftIcon } from 'lucide-react';
import { bonuses, problemCards, volumes } from '../data/content';
import { HeroSection } from '../components/HeroSection';
import { ProblemCard } from '../components/ProblemCard';
import { ProductIntro } from '../components/ProductIntro';
import { VolumeCard } from '../components/VolumeCard';
import { InsideLookSection } from '../components/InsideLookSection';
import { BonusCard } from '../components/BonusCard';
import { BenefitsGrid } from '../components/BenefitsGrid';
import { OfferSection } from '../components/OfferSection';
import { GuaranteeSection } from '../components/GuaranteeSection';
import { AudienceSection } from '../components/AudienceSection';
import { CtaButton } from '../components/CtaButton';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { FAQAccordion } from '../components/FAQAccordion';
import { FinalCTA } from '../components/FinalCTA';
import { Footer } from '../components/Footer';
import { PurchasePopup } from '../components/PurchasePopup';
type LandingPageProps = {
  guaranteeEnabled: boolean;
  testimonialsEnabled: boolean;
  bonusReferencePricesEnabled: boolean;
  salesPopupEnabled: boolean;
};
export function LandingPage({
  guaranteeEnabled,
  testimonialsEnabled,
  bonusReferencePricesEnabled,
  salesPopupEnabled
}: LandingPageProps) {
  return <div className="min-h-screen w-full bg-slate-200 font-sans text-slate-600" data-id="element-240" data-mp="3i9eyzenyzzhp">
      <main className="mx-auto w-full max-w-[768px] overflow-hidden bg-white" data-id="element-241" data-mp="3ibiy7jaxcabn">
        <HeroSection guaranteeEnabled={guaranteeEnabled} data-id="element-242" data-mp="3n6yboa50s47j" />

        <section className="space-y-2.5 bg-slate-50 px-4 py-4 md:px-12 md:py-6" data-id="element-243" data-mp="3r9la4tw74ndp">
          {problemCards.map(c => <ProblemCard key={c.title} {...c} data-id="element-244" data-mp="2qqdetpou7a" />)}
          <div className="pt-2.5" data-id="element-245" data-mp="1wwbu0j95a3mh">
            <CtaButton mode="scroll" subtext="7 volumes + 5 FREE bonuses — see today's launch price" data-id="element-246" data-mp="1wx549u7bbb9d">
              Show me the system built for my patients
            </CtaButton>
          </div>
        </section>

        <ProductIntro data-id="element-247" data-mp="356m3u0f6z10n" />

        <section className="bg-white px-4 pb-6 md:px-12 md:pb-8" data-id="element-248" data-mp="3586q72rcrdnf">
          <h2 className="text-center text-[24px] font-extrabold uppercase leading-[1.15] tracking-[-0.3px] text-blue-950 md:text-[32px]" data-id="element-249" data-mp="308asfl990igb">
            What's Included
          </h2>
          <div className="mt-3 space-y-2.5" data-id="element-250" data-mp="397ebip8f0xut">
            {volumes.map(({
            number,
            ...v
          }) => <VolumeCard key={number} label={`Volume ${number}`} {...v} data-id="element-251" data-mp="3o453idfrf7ae" />)}
          </div>
          <div className="mt-5 text-center" data-id="element-252" data-mp="3j4sizzcd2caz">
            <p className="text-[14px] font-extrabold uppercase leading-tight text-blue-950 md:text-[16px]" data-id="element-253" data-mp="3j3r3niobb3ng">
              Stop building the same exercise guides and protocols from scratch.
            </p>
            <div className="mt-2.5" data-id="element-254" data-mp="3sy10bckaeo3z">
              <CtaButton mode="scroll" subtext="Get all 7 volumes + 5 FREE bonuses for just $26.90 USD" data-id="element-255" data-mp="ap8ufdmmxi29">
                Yes! I want the complete system!
              </CtaButton>
            </div>
          </div>
        </section>

        <InsideLookSection data-id="element-256" data-mp="pda6iys81iog" />

        <section className="bg-blue-950 px-4 py-6 md:px-12 md:py-8" data-id="element-257" data-mp="pawg538hkun8">
          <h2 className="flex items-center justify-center gap-2 text-center text-[21px] font-extrabold uppercase leading-[1.15] tracking-[-0.3px] text-amber-400 md:text-[32px]" data-id="element-258" data-mp="ubbspsky1y45">
            <GiftIcon className="h-6 w-6 shrink-0 md:h-8 md:w-8" strokeWidth={2.5} aria-hidden data-id="element-259" data-mp="kcmlctqobw25" />
            Plus, get 5 professional bonuses free!
          </h2>
          <p className="mx-auto mt-1.5 max-w-[560px] text-center text-[14px] leading-[1.4] text-blue-200 md:text-[15px]" data-id="element-260" data-mp="bczis4m7kk7i">
            Five complementary collections for problem-solving, exercise modification, functional testing, and home
            rehabilitation.
          </p>
          <div className="mt-3 space-y-2.5" data-id="element-261" data-mp="1ktpmrk7zwqq">
            {bonuses.map(b => <BonusCard key={b.number} {...b} showReferencePrice={bonusReferencePricesEnabled} data-id="element-262" data-mp="3jtksjnmnzhbo" />)}
          </div>
        </section>

        <BenefitsGrid data-id="element-263" data-mp="3gqwqnwry6kq7" />
        <OfferSection showReferencePrices={bonusReferencePricesEnabled} guaranteeEnabled={guaranteeEnabled} data-id="element-264" data-mp="3gq4y6utnd345" />
        {guaranteeEnabled && <GuaranteeSection data-id="element-265" data-mp="3bsv8rf69crgb" />}
        <AudienceSection data-id="element-266" data-mp="371d8yufz0wwx" />
        {testimonialsEnabled && <TestimonialsSection data-id="element-267" data-mp="370238p2ytf36" />}
        <FAQAccordion data-id="element-268" data-mp="36z0ifsw5de89" />
        <FinalCTA guaranteeEnabled={guaranteeEnabled} data-id="element-269" data-mp="322jsct5e0psj" />
        <Footer data-id="element-270" data-mp="3220bzgvdy9gm" />
      </main>
      {salesPopupEnabled && <PurchasePopup data-id="element-271" data-mp="2y183wqy92alx" />}
    </div>;
}